import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getPartById,
  updatePart,
  addPart,
  getAllPartCategories,
  getAllPartBrands,
} from "@/services/partsService";
import type { PartCategory, PartBrand } from "@oriental-ramirez/shared";
import { toast } from "sonner";
import { ImageUploadField } from "@/components/ImageUploadField";
import { queryKeys } from "@/lib/queryKeys";

const schema = z.object({
  name: z.string().min(1, "Requerido"),
  sku: z.string().optional(),
  description: z.string().optional(),
  categoryId: z.string().min(1, "Selecciona categoría"),
  brandId: z.string().optional(),
  price: z.coerce.number().min(0, "Debe ser >= 0"),
  currency: z.enum(["USD", "DOP"]).optional(),
  stock: z.coerce.number().optional(),
});

type FormData = z.infer<typeof schema>;

export function PartForm() {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const { data: categories = [] } = useQuery({
    queryKey: queryKeys.partCategories,
    queryFn: getAllPartCategories,
  });
  const { data: brands = [] } = useQuery({
    queryKey: queryKeys.partBrands,
    queryFn: getAllPartBrands,
  });
  const [loading, setLoading] = useState(isEdit);
  const [images, setImages] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { currency: "USD" },
  });

  const categoryId = watch("categoryId");
  const brandId = watch("brandId");

  useEffect(() => {
    if (!id) return;
    getPartById(id).then((p) => {
      if (p) {
        setValue("name", p.name ?? "");
        setValue("sku", p.sku ?? "");
        setValue("description", p.description ?? "");
        setValue("categoryId", (p.category as PartCategory)?.id ?? (p.categoryId as string) ?? "");
        setValue("brandId", (p.brand as PartBrand)?.id ?? (p.brandId as string) ?? "");
        setValue("price", p.price ?? 0);
        setValue("currency", (p.currency as "USD" | "DOP") ?? "USD");
        setValue("stock", p.stock ?? undefined);
        setImages(p.images ?? []);
      }
      setLoading(false);
    });
  }, [id, setValue]);

  const onSubmit = async (data: FormData) => {
    try {
      const category = categories.find((c) => c.id === data.categoryId);
      const brand = data.brandId ? brands.find((b) => b.id === data.brandId) : undefined;
      const payload = {
        name: data.name,
        sku: data.sku,
        description: data.description,
        category,
        categoryId: data.categoryId,
        brand,
        brandId: data.brandId,
        price: data.price,
        currency: data.currency ?? "USD",
        stock: data.stock,
        images,
      };
      if (isEdit && id) {
        await updatePart(id, payload);
        toast.success("Repuesto actualizado");
      } else {
        await addPart(payload as Parameters<typeof addPart>[0]);
        toast.success("Repuesto creado");
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.parts });
      navigate("/parts");
    } catch {
      toast.error("Error al guardar");
    }
  };

  if (isEdit && loading) return <p className="text-muted-foreground">Cargando…</p>;

  return (
    <div className="max-w-5xl space-y-6">
      <h1 className="font-display text-2xl font-semibold">{isEdit ? "Editar repuesto" : "Nuevo repuesto"}</h1>
      <div className="flex flex-col gap-6 lg:flex-row">
        <Card className="min-w-0 flex-1">
          <CardHeader>
            <CardTitle>Datos del repuesto</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
              <Label>Nombre</Label>
              <Input {...register("name")} placeholder="Ej. Filtro de aceite" />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label>SKU</Label>
              <Input {...register("sku")} placeholder="Opcional" />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input {...register("description")} placeholder="Opcional" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select value={categoryId} onValueChange={(v) => setValue("categoryId", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.categoryId && <p className="text-sm text-destructive">{errors.categoryId.message}</p>}
              </div>
              <div className="space-y-2">
                <Label>Marca</Label>
                <Select value={brandId && brandId !== "__none__" ? brandId : undefined} onValueChange={(v) => setValue("brandId", v === "__none__" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Opcional" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {brands.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Precio</Label>
                <Input type="number" step="0.01" {...register("price")} />
                {errors.price && <p className="text-sm text-destructive">{errors.price.message}</p>}
              </div>
              <div className="space-y-2">
                <Label>Stock</Label>
                <Input type="number" {...register("stock")} />
              </div>
            </div>
            <div className="space-y-2">
                <Label>Moneda</Label>
                <Select value={watch("currency")} onValueChange={(v) => setValue("currency", v as "USD" | "DOP")}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="DOP">DOP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Guardando…" : "Guardar"}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate("/parts")}>
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        <aside className="w-full shrink-0 lg:sticky lg:top-6 lg:w-80 lg:self-start">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Fotos del repuesto</CardTitle>
            </CardHeader>
            <CardContent>
              <ImageUploadField
                value={images}
                onChange={setImages}
                folder="parts"
                label=""
              />
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}
